import { Close } from '@mui/icons-material';
import HistoryIcon from '@mui/icons-material/History';
import { Box, IconButton } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { getEntityRecord } from 'lib/api/record';
import { EntityRecord } from 'lib/api/record';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import Title from 'lib/designSystem/Title';
import { CreditLine, Warranty } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hideEntityRecord } from 'services/redux/entityRecordSlice';
import { RootState } from 'services/redux/store';

import StatusRecord from './Components/StatusRecord';

export type EntityRecordType =
  | EntityRecord<CreditLine>
  | EntityRecord<Warranty>
  | EntityRecord<unknown>;

function getEntityRecordTitle(
  type: string | undefined,
  id: number | undefined,
) {
  if (!type || !id) return 'Historial';
  switch (type) {
    case 'CreditLine':
      return `Historial Línea de crédito ${id}`;
    case 'SGRWarrantyRequest':
      return `Historial Garantía ${id}`;
    default:
      return `Historial ${id}`;
  }
}

const EntityRecordModal = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState<EntityRecordType | null>(null);
  const { open, entity } = useSelector(
    (state: RootState) => state.entityRecord,
  );

  const entityValid = useMemo(() => {
    return entity?.type !== undefined && entity?.id !== undefined;
  }, [entity]);

  const handleClose = useCallback(() => {
    dispatch(hideEntityRecord());
  }, []);

  const fetchEntityRecord = useCallback(async () => {
    try {
      setLoading(true);
      if (!entity?.type || !entity?.id) return;
      const response = await getEntityRecord(
        dispatch,
        entity?.type,
        entity?.id,
      );
      switch (entity?.type) {
        case 'CreditLine':
          if (response) setRecord(response as EntityRecord<CreditLine>);
          break;
        case 'SGRWarrantyRequest':
          if (response) setRecord(response as EntityRecord<Warranty>);
          break;
        default:
          if (response) setRecord(response as EntityRecord<unknown>);
          break;
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [entity, dispatch]);

  useEffect(() => {
    void fetchEntityRecord();
  }, [fetchEntityRecord]);

  const MainContent = useMemo(() => {
    switch (entity?.type) {
      case 'CreditLine':
        return <StatusRecord record={record as EntityRecord<CreditLine>} />;
      case 'SGRWarrantyRequest':
        return <StatusRecord record={record as EntityRecord<Warranty>} />;
      default:
        return <></>;
    }
  }, [entity, record]);

  if (!open || !entityValid) return null;

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box sx={{ position: 'relative', px: 2, pt: 2 }}>
        <Title
          title={getEntityRecordTitle(entity?.type, entity?.id)}
          icon={<HistoryIcon color="primary" />}
          color="primary"
        />
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: 'absolute', right: 20, top: 20 }}
          aria-label="close"
        >
          <Close />
        </IconButton>
      </Box>
      {MainContent}
    </Dialog>
  );
};

export default EntityRecordModal;
